import { useMemo, useState } from "react";

import { DateTime } from "luxon";

import {
  ArrowPathIcon,
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  CheckCircleIcon,
  CurrencyPoundIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  MinusCircleIcon,
  PencilSquareIcon,
  SparklesIcon,
  UserCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

import useOffers from "../../data/useOffers";
import useBatchListingsMutations from "../../data/useBatchListingsMutations";
import usePartDetails from "../../data/usePartDetails";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { useUserLookup } from "../../hooks/useUserLookup";
import Modal from "../../layouts/Modal";
import { FormatType } from "../../models/EbayOffer";
import { IPart } from "../../models/Part";
import checkEbayDisabled from "../../utils/checkEbayDisabled";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import openInNewTab from "../../utils/openInNewTab";
import PriceFormat from "../../utils/priceFormat";
import Tooltip from "../shared/Tooltip";
import EditListingSpecifics from "./EditListingSpecifics";
import ListingPoliciesButton from "./ListingPoliciesButton";
import { computeListingScore } from "./listedTable/listingScore";
import { VIEWS_WINDOW_DAYS, computeListingViews } from "./listedTable/listingViews";

const RELIST_AGE_DAYS = 30;
const PRICE_VARIANCE_THRESHOLD = 0.1;
const OVERSUPPLY_MIN_ACTIVE = 6;
const OVERSUPPLY_RATIO = 3;
const DISCOUNT_PERCENT = 0.1;
const LEARNING_AGE_DAYS = 2;

export default function ListingRecommendationsCell({ part }: { part: IPart }) {
  const { update } = useOffers();
  const { bulkRelist } = useBatchListingsMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Listing");
  const { findUser } = useUserLookup();
  const [specificsOpen, setSpecificsOpen] = useState(false);

  const { percent, present, total, missing, breakdown } = computeListingScore(part);
  const presentWeight = breakdown.reduce((sum, field) => sum + (field.present ? field.weight : 0), 0);
  const totalWeight = breakdown.reduce((sum, field) => sum + (field.applicable ? field.weight : 0), 0);
  const { views30d, viewsPerDay, performance } = computeListingViews(part);
  const offer = part.offers?.[0];

  const needsDetails = !Array.isArray(part.images) || !Array.isArray(part.aspects);
  const { partDetails } = usePartDetails(part.id, needsDetails);
  const fullPart = (needsDetails ? partDetails : null) ?? part;

  const ebayCheck = useMemo(() => {
    if (!Array.isArray(fullPart.images) || !Array.isArray(fullPart.offers)) return null;
    try {
      return checkEbayDisabled({
        part: fullPart,
        offer: fullPart.offers?.[0] ?? null,
        ignoreListingTime: true,
      });
    } catch {
      return null;
    }
  }, [fullPart]);

  const relistBlocked = !!ebayCheck?.isDisabled && !!ebayCheck?.error;
  const relistError = ebayCheck?.error ?? "";

  const ageSource = offer?.listingStarted ?? part.created;
  const ageDays = ageSource
    ? Math.floor(DateTime.now().diff(DateTime.fromISO(ageSource, { zone: "utc" }), "days").days)
    : null;

  const currentPrice = offer
    ? offer.format === FormatType.Auction
      ? offer.auctionStartPrice
      : offer.buyItNowPrice
    : null;

  const intelligencePrice = part.intelligenceAverage;

  const priceVariance =
    currentPrice && intelligencePrice && intelligencePrice > 0
      ? Math.abs(currentPrice - intelligencePrice) / intelligencePrice
      : null;

  const shouldRelist = ageDays != null && ageDays > RELIST_AGE_DAYS;
  const shouldUpdatePrice = priceVariance != null && priceVariance > PRICE_VARIANCE_THRESHOLD;

  const isLearning = ageDays != null && ageDays < LEARNING_AGE_DAYS;

  const activeCount = part.intelligenceActiveCount ?? null;
  const soldCount = part.intelligenceSoldCount ?? null;
  const isOversupplied =
    !isLearning &&
    activeCount != null &&
    soldCount != null &&
    activeCount >= OVERSUPPLY_MIN_ACTIVE &&
    activeCount >= Math.max(soldCount, 1) * OVERSUPPLY_RATIO;

  const discountedPrice =
    currentPrice != null ? Math.round(currentPrice * (1 - DISCOUNT_PERCENT) * 100) / 100 : null;

  const handleDiscount = () => {
    if (!offer || discountedPrice == null) return;

    const updatedOffer = { ...offer };
    if (offer.format === FormatType.Auction) {
      updatedOffer.auctionStartPrice = discountedPrice;
    } else {
      updatedOffer.buyItNowPrice = discountedPrice;
    }

    update.mutate(updatedOffer, {
      ...saveHandlers,
      onSuccess: () => {
        if (offer.format === FormatType.Auction) offer.auctionStartPrice = discountedPrice;
        else offer.buyItNowPrice = discountedPrice;
        saveHandlers.onSuccess();
      },
    });
  };

  const handleRelist = () => {
    bulkRelist.mutate({ partIds: [part.id] }, saveHandlers);
  };

  const handleUpdatePrice = () => {
    if (!offer || intelligencePrice == null) return;

    const price = Math.round(intelligencePrice * 100) / 100;
    const updatedOffer = { ...offer };
    if (offer.format === FormatType.Auction) {
      updatedOffer.auctionStartPrice = price;
    } else {
      updatedOffer.buyItNowPrice = price;
    }

    update.mutate(updatedOffer, {
      ...saveHandlers,
      onSuccess: () => {
        if (offer.format === FormatType.Auction) offer.auctionStartPrice = price;
        else offer.buyItNowPrice = price;
        saveHandlers.onSuccess();
      },
    });
  };

  const scoreColor =
    percent >= 80
      ? "bg-green-100 text-green-700"
      : percent >= 50
        ? "bg-amber-100 text-amber-700"
        : "bg-red-100 text-red-700";

  const scoreBreakdownTooltip = (
    <div className="flex flex-col gap-1 text-left">
      <div className="mb-0.5 flex items-center justify-between gap-4 font-semibold">
        <span>Listing score</span>
        <span>{percent}%</span>
      </div>
      {breakdown.map((field) => (
        <div key={field.key} className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-1.5">
            {!field.applicable ? (
              <MinusCircleIcon className="h-3.5 w-3.5 text-gray-500" />
            ) : field.present ? (
              <CheckCircleIcon className="h-3.5 w-3.5 text-green-400" />
            ) : (
              <XCircleIcon className="h-3.5 w-3.5 text-red-400" />
            )}
            <span className={classNames(field.present ? "text-white" : "text-gray-400")}>
              {field.label}
            </span>
          </span>
          <span className="tabular-nums text-gray-400">
            {!field.applicable
              ? "n/a"
              : `${field.present ? `+${field.weight}` : "0"} / ${field.weight}`}
          </span>
        </div>
      ))}
      <div className="mt-1 border-t border-gray-700 pt-1 text-[11px] text-gray-400">
        Weighted: {presentWeight}/{totalWeight} points
      </div>
    </div>
  );

  const ageColor =
    ageDays == null
      ? "bg-gray-100 text-gray-600"
      : ageDays > 90
        ? "bg-red-100 text-red-700"
        : ageDays > RELIST_AGE_DAYS
          ? "bg-amber-100 text-amber-700"
          : "bg-green-100 text-green-700";

  const viewsPerformance: Record<
    typeof performance,
    { color: string; label: string; icon: typeof EyeIcon }
  > = {
    high: { color: "bg-green-100 text-green-700", label: "High", icon: ArrowTrendingUpIcon },
    average: { color: "bg-gray-100 text-gray-600", label: "Steady", icon: EyeIcon },
    low: { color: "bg-red-100 text-red-700", label: "Low", icon: ArrowTrendingDownIcon },
    new: { color: "bg-indigo-100 text-indigo-700", label: "New", icon: SparklesIcon },
  };
  const viewsMeta = viewsPerformance[performance];
  const ViewsIcon = viewsMeta.icon;
  const viewsTooltip =
    performance === "new"
      ? "Too new to rate — gathering views."
      : `~${viewsPerDay.toFixed(1)} views/day over the last ${VIEWS_WINDOW_DAYS} days`;

  const hasActions =
    shouldRelist ||
    shouldUpdatePrice ||
    missing.length > 0 ||
    isOversupplied ||
    isLearning ||
    relistBlocked;

  const listedByName = offer?.publishedBy ? findUser(offer.publishedBy)?.fullname : null;
  const listedDate = offer?.listingStarted
    ? formatDateTime(offer.listingStarted, DateTime.DATE_MED)
    : null;
  const ageTooltip = listedByName
    ? `Listed by ${listedByName}${listedDate ? ` on ${listedDate}` : ""}`
    : listedDate
      ? `Listed on ${listedDate}`
      : "Listing date unknown";

  return (
    <div
      className="flex h-full w-full min-w-[26rem] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white text-xs shadow-sm"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50/80 px-3 py-2">
        <img src="/ebayLogo.svg" alt="eBay" className="h-4 w-4" />
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          eBay Snapshot
        </span>
        {!hasActions && (
          <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-700">
            <CheckCircleIcon className="h-3.5 w-3.5" />
            Optimised
          </span>
        )}
      </div>

      {/* Views cell hidden until eBay analytics endpoint is available — restore grid-cols-3 and uncomment the cell below */}
      <div className="grid grid-cols-2 gap-2 px-3 py-2.5">
        <div className="flex flex-col gap-1 rounded-lg bg-gray-50 px-2.5 py-2">
          <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
            Listing Score
          </span>
          <Tooltip content={scoreBreakdownTooltip}>
            <div className="flex cursor-help items-baseline gap-1.5">
              <span className={classNames("rounded-full px-2 py-0.5 text-sm font-bold", scoreColor)}>
                {percent}%
              </span>
              <span className="text-[11px] text-gray-400">
                {present}/{total}
              </span>
            </div>
          </Tooltip>
        </div>
        <div className="flex flex-col gap-1 rounded-lg bg-gray-50 px-2.5 py-2">
          <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
            Age
          </span>
          <div className="flex min-w-0 items-center gap-1.5">
            <span className={classNames("shrink-0 rounded-full px-2 py-0.5 text-sm font-bold", ageColor)}>
              {ageDays != null ? `${ageDays}d` : "—"}
            </span>
            {listedByName && (
              <Tooltip content={ageTooltip}>
                <UserCircleIcon
                  className="h-4 w-4 shrink-0 text-gray-400 transition hover:text-gray-600"
                  aria-label={`Listed by ${listedByName}`}
                />
              </Tooltip>
            )}
          </div>
        </div>
        {/* Views (30d) cell — restore when eBay analytics endpoint is available
        <div className="flex flex-col gap-1 rounded-lg bg-gray-50 px-2.5 py-2">
          <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
            Views (30d)
          </span>
          <Tooltip content={viewsTooltip}>
            <div className="flex cursor-help min-w-0 items-center gap-1.5">
              <span className="shrink-0 text-sm font-bold tabular-nums text-gray-700">
                {views30d.toLocaleString("en-GB")}
              </span>
              <span
                className={classNames(
                  "inline-flex shrink-0 items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold",
                  viewsMeta.color
                )}
              >
                <ViewsIcon className="h-3 w-3" />
                {viewsMeta.label}
              </span>
            </div>
          </Tooltip>
        </div>
        */}
      </div>

      <div className="mt-auto grid grid-cols-2 gap-1.5 border-t border-gray-100 px-3 py-2.5">
        {relistBlocked && (
          <div className="col-span-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-red-800">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
            <span className="flex flex-col">
              <span className="font-semibold">Can&apos;t relist</span>
              <span className="text-[11px] text-red-700/90">{relistError}</span>
            </span>
          </div>
        )}

        {isLearning && (
          <div className="col-span-2 flex items-start gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1.5 text-indigo-800">
            <SparklesIcon className="mt-0.5 h-4 w-4 shrink-0 animate-pulse" />
            <span className="flex flex-col">
              <span className="font-semibold">Wilma Intelligence</span>
              <span className="text-[11px] text-indigo-700/90">
                Learning — gathering sales data on this listing.
              </span>
            </span>
          </div>
        )}

        {isOversupplied && (
          <div className="col-span-2 flex flex-col gap-1.5 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1.5 text-indigo-800 sm:flex-row sm:items-center">
            <div className="flex flex-1 items-start gap-2">
              <SparklesIcon className="mt-0.5 h-4 w-4 shrink-0" />
              <span className="flex flex-col">
                <span className="font-semibold">Wilma Intelligence</span>
                <span className="text-[11px] text-indigo-700/90">
                  {activeCount} listed vs {soldCount} sold — high supply, low demand. Consider discounting.
                </span>
              </span>
            </div>
            {discountedPrice != null && (
              <button
                type="button"
                onClick={handleDiscount}
                disabled={update.isLoading}
                className="flex shrink-0 items-center justify-center gap-1.5 rounded-md border border-indigo-300 bg-white px-2 py-1 font-semibold text-indigo-700 transition hover:bg-indigo-100 disabled:opacity-60"
              >
                <CurrencyPoundIcon className="h-4 w-4 shrink-0" />
                Discount 10% → {PriceFormat(discountedPrice)}
              </button>
            )}
          </div>
        )}

        {shouldRelist && (
          <button
            type="button"
            onClick={handleRelist}
            disabled={bulkRelist.isLoading || relistBlocked}
            title={relistBlocked ? relistError : undefined}
            className="flex items-center gap-2 rounded-md border border-sky-200 bg-sky-50 px-2 py-1.5 text-left text-sky-800 transition hover:bg-sky-100 disabled:opacity-60"
          >
            <ArrowPathIcon className={classNames("h-4 w-4 shrink-0", bulkRelist.isLoading && "animate-spin")} />
            <span className="flex flex-col">
              <span className="font-semibold">Relist</span>
              <span className="text-[11px] text-sky-700/80">Listed {ageDays} days ago</span>
            </span>
          </button>
        )}

        {shouldUpdatePrice && (
          <button
            type="button"
            onClick={handleUpdatePrice}
            disabled={update.isLoading}
            className="flex items-center gap-2 rounded-md border border-violet-200 bg-violet-50 px-2 py-1.5 text-left text-violet-800 transition hover:bg-violet-100 disabled:opacity-60"
          >
            <CurrencyPoundIcon className="h-4 w-4 shrink-0" />
            <span className="flex min-w-0 flex-col">
              <span className="truncate font-semibold">Update price → {PriceFormat(intelligencePrice!)}</span>
              <span className="text-[11px] text-violet-700/80">
                {Math.round(priceVariance! * 100)}% off intelligence avg
              </span>
            </span>
          </button>
        )}

        {missing.length > 0 ? (
          <button
            type="button"
            onClick={() => setSpecificsOpen(true)}
            className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-left text-amber-800 transition hover:bg-amber-100"
          >
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
            <span className="flex min-w-0 flex-col">
              <span className="font-semibold">Add missing fields</span>
              <span className="truncate text-[11px] text-amber-700/90">{missing.join(", ")}</span>
            </span>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setSpecificsOpen(true)}
            title="Edit item specifics"
            aria-label="Edit item specifics"
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1.5 font-medium text-slate-600 transition hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
          >
            <PencilSquareIcon className="h-4 w-4 shrink-0" />
            <span>Item specifics</span>
          </button>
        )}

        

        <ListingPoliciesButton part={part} />

        {part.ebayItemNumber && (
          <button
            type="button"
            title="View item on eBay"
            onClick={() => openInNewTab(`https://www.ebay.co.uk/itm/${part.ebayItemNumber}`)}
            className="flex items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1.5 font-medium text-primary-600 transition hover:border-primary-300 hover:bg-primary-50 hover:text-primary-800"
          >
            <img src="/ebayLogo.svg" alt="eBay" className="h-4 w-4" />
            View on eBay
          </button>
        )}
      </div>

      <Modal
        key={`snapshot-specifics-${part.id}`}
        open={specificsOpen}
        setOpen={setSpecificsOpen}
        width="max-w-7xl"
      >
        {specificsOpen && <EditListingSpecifics part={part} />}
      </Modal>
    </div>
  );
}
